import { DocumentComparer } from '@app/routes/Dashboard/Dpo/DocumentComparer';
import OutputView from '@components/block/OutputView/OutputView';
import { CloseButton } from '@components/ui/CloseButton/CloseButton';
import { Modal, Paper } from '@mui/material';

import { useMessages } from '@app/context/MessageContext';
import {
  OnboardingState,
  useOnboardingActions,
  useOnboardingStateMachine,
} from '@app/context/OnboardingStateMachineContext';
import { useUser } from '@app/context/UserContext';
import { useNavigation } from '@app/hooks/useNavigation';
import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { UserQuestionnaireApplication } from '@dieterApi/user/useUserQuery';
import { QuestionnaireState } from '@legalosApi/types/QuestionnaireState';
import cx from 'classnames';
import { animated, useSpring } from 'react-spring';
import './documentitem.sass';

export function DocumentPaper() {
  const { user } = useUser();
  const { activeCompany } = useUserWithCompany();
  const { userValues } = useUserValuesData();
  const { clickCTA } = useOnboardingActions();
  const { currentState } = useOnboardingStateMachine();
  const {
    navigation: { isMobile, documentPaper, dashboardLocked, roadBlock, roadBlockDSFA },
    setNavigation,
  } = useNavigation();
  const { celloreminder } = useMessages();
  const { questionnaire, index, modalOpen } = documentPaper;
  const animatePaper = useSpring({
    transform:
      (!user?.email || !user.confirmed || roadBlock.modalOpen || roadBlockDSFA.modalOpen) && !isMobile
        ? 'translateX(30%)'
        : 'translateX(0%)',
  });

  const appIsPublic = questionnaire?.application.tags?.map((t) => t.tag).includes('public');

  const onClose = () => {
    setNavigation(
      (nav) => void ((nav.documentPaper.modalOpen = false), (nav.justFinishedQuestionnaireRemoteId = null))
    );

    if (currentState === OnboardingState.DocumentPreviewWithCTA) {
      console.log('[DocumentCallToAction] CTA clicked, dispatching event', { currentState });
      clickCTA();
    }

    user?.isSubscriber && celloreminder.setContext('celloreminder');
  };

  // after a questionnaire has been opened, it is possibly recreated and could be marked as UNTOUCHED or INCOMPLETE
  // due to a version update. In this case we need to close the document and inform the user about the update.
  const handleOpenQuestionnaire = (localQuestionnaire: UserQuestionnaireApplication) => {
    // we need to exclude the dieter AVV questionnaire from this check, because it should never be visited by a user
    const dieterAV = userValues?.find(({ value, key }) => key === 'DATA_PROCESSOR_CONTRACT_LOCKED' && value === 'true');

    if (
      localQuestionnaire.state !== QuestionnaireState.COMPLETE &&
      !dashboardLocked &&
      localQuestionnaire.userValueIndex !== (dieterAV?.index || '')
    ) {
      onClose();
      setNavigation((nav) => {
        nav.questionnaireHasChanged = {
          modalOpen: true,
          questionnaire: localQuestionnaire,
        };
      });
    }
  };

  // Render DocumentComparer with its own modal for DpoManaged companies
  if (modalOpen && questionnaire && activeCompany?.features?.includes('DpoManaged')) {
    return (
      <DocumentComparer
        onClose={onClose}
        localQuestionnaireId={questionnaire.id}
        documentNumber={questionnaire.application.splitDocument ? index + 1 : undefined}
      />
    );
  }

  // Render standard OutputView in DocumentPaper's modal for non-DpoManaged companies
  return (
    <Modal open={modalOpen} onClose={onClose} sx={{ zIndex: 30 }} disableAutoFocus>
      <div className="pointer-events-none">
        <animated.div style={animatePaper} className="h-screen relative pointer-events-none">
          <Paper
            className={cx(
              !questionnaire && 'hidden',
              'absolute top-1/2 left-1/2 overflow-y-auto overflow-x-hidden h-full md:h-[90vh] w-full md:w-[80vw] max-w-[800px] -translate-y-1/2 -translate-x-1/2 z-30 pointer-events-auto'
            )}
            elevation={10}
          >
            {user?.email && <CloseButton onClick={onClose} offset={false} btnColor="white" />}
            {questionnaire && (
              <OutputView
                localQuestionnaireId={questionnaire.id}
                documentNumber={questionnaire.application.splitDocument ? index + 1 : undefined}
                onClose={onClose}
                locked={
                  dashboardLocked || roadBlock.modalOpen || (!appIsPublic && (!user?.isSubscriber || user?.isTrialing))
                }
                trialing={user?.isTrialing}
                onOpenQuestionnaire={handleOpenQuestionnaire}
              />
            )}
          </Paper>
        </animated.div>
      </div>
    </Modal>
  );
}
